import React, { useCallback, useMemo, ReactNode } from 'react';
import { assign, createMachine, fromPromise } from 'xstate';
import { useMachine } from '@xstate/react';
import { firstValueFrom } from 'rxjs';
import { Button } from 'src/components/Button/Button';
import { ModalBody } from 'src/components/Modal/ModalBody';
import { Modal } from 'src/components/Modal/Modal';
import { useDeps } from 'src/hooks/useDeps';
import { useCustomerStatus } from 'src/core/state/user';
import { submitService } from 'src/components/BasketShared/services/submit.service';
import { Body } from 'swagger/ts/WorkwearExpressSharedBasketSharedBasketCreatorV1CreatePost';
import { useCspLoggedInToken } from 'src/components/CustomerServicePortal/hooks/useCspLogin';
import { UserKind } from 'src/components/BasketShared/ShareBasketViaEmailForm';
import { getGlobalInspector } from 'src/components/Inspect/Inspect';

import { ShareForm } from './ShareForm';
import { ShareFormSuccess } from './ShareFormSuccess';

type FormValues = {
    name: string;
    creatorEmail: string;
    collaboratorEmail: string;
    message: string;
    mobileNumber?: string;
};

type MachineInput = {
    cartId: string;
    isSignedIn: boolean;
    cspToken: string | null;
    deps: ReturnType<typeof useDeps>;
};

type Context = MachineInput & {
    error: string | null;
    isSharedEmail: boolean;
    formValues: FormValues;
};

type Events =
    { type: 'OPEN_POPUP' } | { type: 'CLOSE_POPUP' } | { type: 'SUBMIT'; values?: FormValues; isSharedEmail?: boolean };

type SubmitInput = MachineInput & {
    body: Body;
};

const initialFormValues: FormValues = {
    name: '',
    creatorEmail: '',
    collaboratorEmail: '',
    message: '',
};

const basketShareMachine = createMachine({
    id: 'BasketShareMachine',
    types: {} as {
        context: Context;
        input: MachineInput;
        events: Events;
    },
    initial: 'idle',
    context: ({ input }) => ({
        error: null,
        isSharedEmail: false,
        formValues: initialFormValues,
        ...input,
    }),
    states: {
        idle: {
            entry: ['assignClearData'],
            on: {
                OPEN_POPUP: 'showPopup',
            },
        },
        showPopup: {
            on: {
                SUBMIT: 'submitting',
                CLOSE_POPUP: 'idle',
            },
        },
        submitting: {
            entry: ['assignFormValue'],
            invoke: {
                src: fromPromise(({ input }: { input: SubmitInput }) =>
                    firstValueFrom(
                        submitService(input.isSignedIn, input.cspToken, input.body, input.cartId, input.deps),
                    ),
                ),
                input: ({ context }) => ({
                    body: {
                        creationRequest: {
                            name: context.formValues.name,
                            creator_email: context.cspToken ? undefined : context.formValues.creatorEmail,
                            collaborator_email: context.formValues.collaboratorEmail,
                            message: context.formValues.message,
                            extension_attributes: {
                                mobile_number: context.cspToken ? context.formValues.mobileNumber : undefined,
                            },
                            async: context.isSharedEmail,
                        },
                    },
                    cartId: context.cartId,
                    isSignedIn: context.isSignedIn,
                    cspToken: context.cspToken,
                    deps: context.deps,
                }),
                onDone: 'success',
                onError: { target: 'error', actions: 'assignError' },
            },
        },
        success: {
            on: {
                CLOSE_POPUP: 'idle',
            },
        },
        error: {
            on: {
                CLOSE_POPUP: 'idle',
            },
        },
    },
}).provide({
    actions: {
        assignFormValue: assign({
            formValues: ({ event }) =>
                event.type === 'SUBMIT' ? (event.values ?? initialFormValues) : initialFormValues,
            isSharedEmail: ({ event }) => (event.type === 'SUBMIT' ? (event.isSharedEmail ?? false) : false),
        }),
        assignClearData: assign({
            error: null,
            formValues: initialFormValues,
            isSharedEmail: false,
        }),
        assignError: assign({
            error: ({ event }) =>
                (event as { error?: { response?: { message?: string } } }).error?.response?.message ?? null,
        }),
    },
});

type Props = {
    cartId: string;
    display: 'inline' | 'modal';
    devTools?: boolean;
    shareButtonContent?: ReactNode;
};

export const BasketShareMachine: React.FC<Props> = ({ cartId, display, devTools, shareButtonContent }) => {
    const deps = useDeps();
    const { isSignedIn } = useCustomerStatus();
    const cspToken = useCspLoggedInToken();

    const machine = useMemo(() => basketShareMachine, []);
    const inspector = getGlobalInspector();
    const [state, send] = useMachine(machine, {
        input: { cartId, isSignedIn, cspToken, deps },
        ...(devTools && inspector ? { inspect: inspector.inspect } : {}),
    });

    const onShare = useCallback(() => {
        send({ type: 'OPEN_POPUP' });
    }, [send]);

    const onCancel = useCallback(() => {
        send({ type: 'CLOSE_POPUP' });
    }, [send]);

    const onSubmit = useCallback(
        (values: FormValues, isSharedEmail?: boolean) => {
            send({ type: 'SUBMIT', values, isSharedEmail });
        },
        [send],
    );

    const isPending = state.value === 'submitting';
    /**
     * xstate v5 `state.matches` is an instance method that reads `this.value`,
     * so passing it bare as the `.some` callback loses the `this` binding and
     * throws `TypeError: can't access property "value", this is undefined`,
     * which the error boundary then unmounts into the white /basket screen.
     * Wrapping in an arrow keeps the binding intact.
     */
    const popupOpen = ['showPopup', 'submitting', 'success', 'error'].some((s) => state.matches(s));

    const button = (
        <Button disabled={isPending} width="full" variant="light" onClick={onShare} data-test-id="ShareBasketTrigger">
            {shareButtonContent}
        </Button>
    );
    const inner = (
        <>
            {state.value !== 'success' && (
                <ShareForm
                    onCancel={onCancel}
                    onSubmit={onSubmit}
                    isPending={isPending}
                    errors={state.context.error}
                    userKind={cspToken ? UserKind.Csp : UserKind.Customer}
                    cartId={cartId}
                />
            )}
            {state.value === 'success' && (
                <ShareFormSuccess
                    onCancel={onCancel}
                    recipientEmail={state.context.formValues.collaboratorEmail}
                    isSharedEmail={state.context.isSharedEmail}
                />
            )}
        </>
    );

    switch (display) {
        case 'inline':
            return (
                <>
                    {button}
                    {popupOpen && inner}
                </>
            );
        case 'modal':
            return (
                <>
                    {button}
                    <Modal type="narrow" isOpen={popupOpen}>
                        <ModalBody>{inner}</ModalBody>
                    </Modal>
                </>
            );
    }
};
